import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Location, Warehouse } from "@/data/mockWarehousesLocations";
import { Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { useWarehouseHierarchyConfig } from "@/hooks/use-warehouse-hierarchy-config";

interface ImportLocationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  warehouses: Warehouse[];
  existingLocations: Location[];
  onComplete: (locations: Location[]) => void;
  isLive?: boolean;
}

export function ImportLocationsDialog({ 
  open, 
  onOpenChange, 
  warehouses,
  existingLocations,
  onComplete,
  isLive = false
}: ImportLocationsDialogProps) {
  const hierarchyConfig = useWarehouseHierarchyConfig({ isLive });
  const [step, setStep] = useState<"upload" | "preview">("upload");
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const handleDownloadTemplate = () => {
    try {
      console.log("Download template clicked");
      console.log("Hierarchy config:", hierarchyConfig);
      console.log("Active levels:", hierarchyConfig.activeLevels);
      
      // Check if hierarchy config is loaded
      if (!hierarchyConfig || !hierarchyConfig.activeLevels || hierarchyConfig.activeLevels.length === 0) {
        console.error("Hierarchy config not loaded properly");
        toast.error("Hierarchy configuration not loaded. Please try again.");
        return;
      }

      // Generate dynamic headers based on hierarchy config
      const hierarchyHeaders = hierarchyConfig.activeLevels
        .filter(level => level.level > 1) // Skip warehouse level (level 1)
        .map(level => level.displayName)
        .join(',');
      
      const headers = `Warehouse Code,${hierarchyHeaders},Type,Picking Sequence`;
      
      // Generate example rows
      const exampleRow1Parts = hierarchyConfig.activeLevels
        .filter(level => level.level > 1)
        .map(level => {
          const prefix = level.codePrefix?.trim() || '';
          return prefix ? `${prefix}01` : '01';
        })
        .join(',');
      
      const exampleRow2Parts = hierarchyConfig.activeLevels
        .filter(level => level.level > 1)
        .map(level => {
          const prefix = level.codePrefix?.trim() || '';
          return prefix ? `${prefix}02` : '02';
        })
        .join(',');
      
      const template = `${headers}
WH-A,${exampleRow1Parts},Storage,1
WH-A,${exampleRow2Parts},Storage,2`;
      
      const blob = new Blob([template], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "locations-import-template.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Template downloaded successfully");
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error("Failed to download template. Please try again.");
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      parseCSV(text);
    };
    reader.readAsText(file);
  };

  const parseCSV = (text: string) => {
    const lines = text.split("\n").filter(line => line.trim());
    if (lines.length < 2) {
      toast.error("File is empty or invalid");
      return;
    }

    const headers = lines[0].split(",").map(h => h.trim());
    const data: any[] = [];
    const errors: string[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(",").map(v => v.trim());
      const row: any = {};
      
      headers.forEach((header, index) => {
        row[header] = values[index] || "";
      });

      // Validate row
      const rowErrors = validateRow(row, i + 1);
      if (rowErrors.length > 0) {
        errors.push(...rowErrors);
      }

      data.push(row);
    }

    setParsedData(data);
    setValidationErrors(errors);
    setStep("preview");
  };

  const validateRow = (row: any, lineNumber: number): string[] => {
    const errors: string[] = [];

    // Check warehouse exists
    if (!row["Warehouse Code"]) {
      errors.push(`Line ${lineNumber}: Warehouse Code is required`);
    } else if (!warehouses.find(w => w.code === row["Warehouse Code"])) {
      errors.push(`Line ${lineNumber}: Warehouse "${row["Warehouse Code"]}" does not exist`);
    }

    // Check location type
    const validTypes = ["Storage", "Picking", "Receiving", "Staging", "Shipping", "Quarantine", "Returns"];
    if (row["Type"] && !validTypes.includes(row["Type"])) {
      errors.push(`Line ${lineNumber}: Invalid location type "${row["Type"]}"`);
    }

    // Generate location code using hierarchy config
    const hierarchyParts: { [key: number]: string } = {};
    hierarchyConfig.activeLevels
      .filter(level => level.level > 1) // Skip warehouse level
      .forEach(level => {
        const value = row[level.displayName];
        if (value && value.trim()) {
          hierarchyParts[level.level] = value.trim();
        }
      });

    const locationCode = hierarchyConfig.generateLocationCode(hierarchyParts);

    if (!locationCode) {
      errors.push(`Line ${lineNumber}: At least one hierarchy level is required`);
    } else if (existingLocations.some(l => l.locationCode === locationCode && l.warehouseCode === row["Warehouse Code"])) {
      errors.push(`Line ${lineNumber}: Location "${locationCode}" already exists in warehouse "${row["Warehouse Code"]}"`);
    }

    return errors;
  };

  const handleImport = () => {
    if (validationErrors.length > 0) {
      toast.error("Please fix validation errors before importing");
      return;
    }

    const newLocations: Location[] = parsedData.map((row, index) => {
      // Build hierarchy parts from row data
      const hierarchyParts: { [key: number]: string } = {};
      const hierarchyData: any = {};
      
      hierarchyConfig.activeLevels
        .filter(level => level.level > 1) // Skip warehouse level
        .forEach(level => {
          const value = row[level.displayName];
          if (value && value.trim()) {
            hierarchyParts[level.level] = value.trim();
            hierarchyData[level.level] = value.trim();
          }
        });

      const locationCode = hierarchyConfig.generateLocationCode(hierarchyParts);

      // Map to legacy fields for backward compatibility
      const legacyFields: any = {};
      hierarchyConfig.activeLevels.forEach(level => {
        const value = hierarchyData[level.level];
        if (level.level === 2) legacyFields.aisle = value;
        else if (level.level === 3) legacyFields.rack = value;
        else if (level.level === 4) legacyFields.shelf = value;
        else if (level.level === 5) legacyFields.bin = value;
      });

      return {
        locationCode,
        warehouseCode: row["Warehouse Code"],
        ...legacyFields,
        hierarchyData,
        locationType: (row["Type"] || "Storage") as Location["locationType"],
        currentStock: 0,
        utilizationPercent: 0,
        pickingSequence: row["Picking Sequence"] ? parseInt(row["Picking Sequence"]) : index + 1,
        active: true,
      };
    });

    onComplete(newLocations);
    onOpenChange(false);
    
    // Reset
    setStep("upload");
    setParsedData([]);
    setValidationErrors([]);
  };

  const handleBack = () => {
    setStep("upload");
    setParsedData([]);
    setValidationErrors([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Locations</DialogTitle>
          <DialogDescription>
            Import multiple locations from a CSV or Excel file
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-6 py-4">
            {/* Download Template */}
            <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="font-medium">Download Template</p>
                  <p className="text-sm text-muted-foreground">
                    Get the CSV template with example data
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={handleDownloadTemplate}
                disabled={hierarchyConfig.isLoading}
              >
                <Download className="h-4 w-4 mr-2" />
                {hierarchyConfig.isLoading ? "Loading..." : "Download"}
              </Button>
            </div>

            {/* Upload Area */}
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="font-semibold mb-2">Upload CSV File</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select a CSV file with your location data
              </p>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload">
                <Button variant="outline" asChild>
                  <span>Choose File</span>
                </Button>
              </label>
            </div>

            {/* Instructions */}
            <div className="space-y-2 text-sm">
              <p className="font-medium">Required Columns:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Warehouse Code (must match existing warehouse)</li>
                <li>At least one hierarchy level ({hierarchyConfig.activeLevels
                  .filter(level => level.level > 1)
                  .map(level => level.displayName)
                  .join(', ')})</li>
                <li>Type (Storage, Picking, Receiving, Staging, Shipping, Quarantine, Returns)</li>
              </ul>
              <p className="font-medium mt-4">Optional Columns:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Picking Sequence (numeric)</li>
              </ul>
            </div>
          </div>
        )}

        {step === "preview" && (
          <div className="space-y-4 py-4">
            {/* Validation Summary */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                {validationErrors.length === 0 ? (
                  <>
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                    <div>
                      <p className="font-medium text-green-600">Validation Passed</p>
                      <p className="text-sm text-muted-foreground">
                        {parsedData.length} locations ready to import
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-6 w-6 text-destructive" />
                    <div>
                      <p className="font-medium text-destructive">Validation Errors</p>
                      <p className="text-sm text-muted-foreground">
                        {validationErrors.length} error(s) found
                      </p>
                    </div>
                  </>
                )}
              </div>
              <Badge variant={validationErrors.length === 0 ? "success" : "destructive"}>
                {parsedData.length} rows
              </Badge>
            </div>

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Errors:</p>
                <ScrollArea className="h-[150px] rounded-md border p-4 bg-destructive/5">
                  <div className="space-y-1">
                    {validationErrors.map((error, index) => (
                      <p key={index} className="text-sm text-destructive">
                        • {error}
                      </p>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* Preview Data */}
            <div className="space-y-2">
              <p className="text-sm font-medium">Preview:</p>
              <ScrollArea className="h-[250px] rounded-md border">
                <div className="p-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Location Code</th>
                        <th className="text-left p-2">Warehouse</th>
                        {hierarchyConfig.activeLevels
                          .filter(level => level.level > 1)
                          .map(level => (
                            <th key={level.level} className="text-left p-2">{level.displayName}</th>
                          ))}
                        <th className="text-left p-2">Type</th>
                        <th className="text-left p-2">Picking Seq</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedData.map((row, index) => {
                        const hierarchyParts: { [key: number]: string } = {};
                        hierarchyConfig.activeLevels
                          .filter(level => level.level > 1)
                          .forEach(level => {
                            const value = row[level.displayName];
                            if (value && value.trim()) {
                              hierarchyParts[level.level] = value.trim();
                            }
                          });
                        const locationCode = hierarchyConfig.generateLocationCode(hierarchyParts);

                        return (
                          <tr key={index} className="border-b">
                            <td className="p-2 font-mono">{locationCode}</td>
                            <td className="p-2">{row["Warehouse Code"]}</td>
                            {hierarchyConfig.activeLevels
                              .filter(level => level.level > 1)
                              .map(level => (
                                <td key={level.level} className="p-2">{row[level.displayName] || "-"}</td>
                              ))}
                            <td className="p-2">{row["Type"] || "Storage"}</td>
                            <td className="p-2">{row["Picking Sequence"] || "-"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </ScrollArea>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "upload" ? (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleBack}>
                Back
              </Button>
              <Button 
                onClick={handleImport} 
                disabled={validationErrors.length > 0 || parsedData.length === 0}
              >
                Import {parsedData.length} Locations
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
